/**
 * TokenManager - Handles dual-token storage and expiration tracking
 * Based on backend integration guide for refresh token implementation
 */

import { logger } from './logger';

interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type?: string;
}

export class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = 'access_token';
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly EXPIRES_AT_KEY = 'expires_at';
  private static readonly USER_KEY = 'user';
  
  // 30 minutes access token duration with 5 minute buffer for preemptive refresh
  private static readonly ACCESS_TOKEN_DURATION = 30 * 60 * 1000; // 30 minutes
  private static readonly REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes buffer

  /**
   * Store both access and refresh tokens with expiration metadata
   */
  static storeTokens(tokens: TokenPair): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);
    
    // Set expiration time (current time + 30 minutes)
    const expiresAt = Date.now() + this.ACCESS_TOKEN_DURATION;
    localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());
    
    logger.tokenInfo('Stored new token pair', { expiresAt: new Date(expiresAt).toLocaleString() }, 'TokenManager');
  }

  /**
   * Get the current access token
   */
  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get the current refresh token
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Check if the access token is expired or will expire soon
   * Uses 5-minute buffer for preemptive refresh
   */
  static isAccessTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
    
    if (!expiresAt) {
      logger.tokenWarn('No expiration time found, considering expired', undefined, 'TokenManager');
      return true;
    }
    
    const expirationTime = parseInt(expiresAt);
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const isExpired = currentTime >= (expirationTime - this.REFRESH_BUFFER);
    
    if (isExpired) {
      logger.tokenInfo('Token expired or expires soon', {
        currentTime: new Date(currentTime).toLocaleString(),
        expirationTime: new Date(expirationTime).toLocaleString(),
        timeUntilExpiry: Math.floor(timeUntilExpiry / 1000 / 60), // minutes
        bufferMinutes: this.REFRESH_BUFFER / 1000 / 60
      }, 'TokenManager');
    }
    
    return isExpired;
  }

  /**
   * Check if we have tokens stored
   */
  static hasTokens(): boolean {
    return !!(this.getAccessToken());
  }

  /**
   * Check if we have a valid (non-expired) access token
   */
  static hasValidAccessToken(): boolean {
    if (!this.hasTokens()) return false;
    return !this.isAccessTokenExpired();
  }

  /**
   * Clear all stored tokens and user data
   */
  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    localStorage.removeItem(this.USER_KEY);
    logger.tokenInfo('Cleared all tokens and user data', undefined, 'TokenManager');
  }

  /**
   * Store user data (for compatibility with existing auth flow)
   */
  static storeUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Get stored user data
   */
  static getUser(): any | null {
    const userData = localStorage.getItem(this.USER_KEY);
    if (userData && userData !== 'undefined' && userData !== 'null') {
      try {
        return JSON.parse(userData);
      } catch (error) {
        logger.tokenError('Error parsing user data', error, 'TokenManager');
        return null;
      }
    }
    return null;
  }

  /**
   * Get token expiration info for debugging
   */
  static getTokenInfo(): any {
    const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
    const hasAccess = !!this.getAccessToken();
    const hasRefresh = !!this.getRefreshToken();
    const isExpired = this.isAccessTokenExpired();
    
    return {
      hasAccessToken: hasAccess,
      hasRefreshToken: hasRefresh,
      isExpired,
      expiresAt: expiresAt ? new Date(parseInt(expiresAt)).toLocaleString() : null,
      timeUntilExpiry: expiresAt ? Math.floor((parseInt(expiresAt) - Date.now()) / 1000 / 60) : null // minutes
    };
  }
}

export default TokenManager;